import { DOCUMENT, NgTemplateOutlet } from '@angular/common';
import { afterNextRender, afterRenderEffect, Component, computed, ElementRef, inject, Injector, input, NgZone, output, signal, untracked, viewChild } from '@angular/core';
import { MenuItem } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { Menu, MenuModule } from 'primeng/menu';

export interface BulkAction {
  id: string;
  label: string;
  icon?: string;
  disabled?: boolean;
  command: () => void;
}

@Component({
  selector: 'app-bulk-action-bar',
  imports: [ButtonModule, MenuModule, NgTemplateOutlet],
  templateUrl: './bulk-action-bar.component.html',
  styleUrl: './bulk-action-bar.component.scss'
})
export class BulkActionBarComponent {
  private static nextId = 0;
  private readonly document = inject(DOCUMENT);
  private readonly injector = inject(Injector);
  private readonly zone = inject(NgZone);
  private readonly container = viewChild<ElementRef<HTMLElement>>('container');
  private readonly toolbar = viewChild<ElementRef<HTMLElement>>('toolbar');
  private readonly measurement = viewChild<ElementRef<HTMLElement>>('measurement');
  private readonly menu = viewChild<Menu>('menu');
  private readonly visibleCount = signal(0);

  public readonly count = input.required<number>();
  public readonly actions = input.required<readonly BulkAction[]>();
  public readonly allSelected = input(false);
  public readonly busy = input(false);
  public readonly selectAll = output();
  public readonly clear = output();
  public readonly menuId = `bulk-action-menu-${String(BulkActionBarComponent.nextId++)}`;
  public readonly menuOpen = signal(false);
  public readonly availableActions = computed<readonly BulkAction[]>(() => [
    ...(!this.allSelected() ? [{
      id: 'bulk-select-all',
      label: $localize`:@@bulk-select-all:Alle auswählen`,
      command: () => this.selectAll.emit()
    }] : []),
    ...this.actions()
  ]);
  public readonly visibleActions = computed(() => this.availableActions().slice(0, this.visibleCount()));
  public readonly overflowActions = computed(() => this.availableActions().slice(this.visibleCount()));
  public readonly menuItems = computed<MenuItem[]>(() => this.overflowActions().map(action => ({
    label: action.label,
    icon: action.icon,
    disabled: this.busy() || action.disabled,
    command: () => this.runAction(action.id)
  })));
  // Parent getters may return fresh action objects; only structural changes need
  // a new observer. Text, fonts and count changes are detected by element sizes.
  private readonly actionIds = computed(() => JSON.stringify(this.availableActions().map(action => action.id)));

  constructor() {
    afterRenderEffect(onCleanup => {
      this.actionIds();
      const container = this.container()?.nativeElement;
      const measurement = this.measurement()?.nativeElement;
      if (!container || !measurement) return;

      this.zone.runOutsideAngular(() => {
        const observer = new ResizeObserver(() => this.updateLayout(container, measurement));
        observer.observe(container);
        observer.observe(measurement);
        for (const child of Array.from(measurement.children)) observer.observe(child);
        onCleanup(() => observer.disconnect());
        untracked(() => this.updateLayout(container, measurement));
      });
    });
  }

  public runAction(id: string): void {
    // Resolve the current action so an open menu cannot execute stale permissions.
    const action = this.availableActions().find(item => item.id === id);
    if (action && !this.busy() && !action.disabled) action.command();
  }

  public clearSelection(): void {
    if (!this.busy()) this.clear.emit();
  }

  private updateLayout(container: HTMLElement, measurement: HTMLElement): void {
    const count = measurement.querySelector<HTMLElement>('.bulk-action-bar-count');
    const more = measurement.querySelector<HTMLElement>('[data-overflow]');
    const close = measurement.querySelector<HTMLElement>('[data-clear]');
    if (!count || !more || !close) return;

    const styles = getComputedStyle(measurement);
    const gap = parseFloat(styles.columnGap);
    const chrome = parseFloat(styles.paddingLeft) + parseFloat(styles.paddingRight)
      + parseFloat(styles.borderLeftWidth) + parseFloat(styles.borderRightWidth);
    const widths = Array.from(measurement.querySelectorAll<HTMLElement>('[data-action-id]'))
      .map(element => element.getBoundingClientRect().width + gap);
    const availableWidth = Math.floor(container.getBoundingClientRect().width);
    const fixedWidth = chrome + count.getBoundingClientRect().width + close.getBoundingClientRect().width + gap;
    let nextCount = widths.length;

    if (Math.ceil(fixedWidth + widths.reduce((sum, width) => sum + width, 0)) > availableWidth) {
      let used = fixedWidth + more.getBoundingClientRect().width + gap;
      nextCount = 0;
      for (const width of widths) {
        if (Math.ceil(used + width) > availableWidth) break;
        used += width;
        nextCount++;
      }
    }

    if (nextCount === this.visibleCount()) return;
    const active = this.document.activeElement;
    const focusedAction = this.toolbar()?.nativeElement.contains(active)
      ? active?.closest('[data-action-id]')?.getAttribute('data-action-id') : undefined;
    const focusedIndex = this.availableActions().findIndex(action => action.id === focusedAction);
    const menu = this.menu();
    const needsFocus = (focusedIndex >= nextCount && focusedIndex >= 0)
      || !!this.document.getElementById(this.menuId)?.contains(active)
      || (nextCount === widths.length && !!this.toolbar()?.nativeElement.querySelector('[data-overflow]')?.contains(active));

    this.zone.run(() => {
      menu?.hide();
      this.menuOpen.set(false);
      this.visibleCount.set(nextCount);
      if (needsFocus) {
        afterNextRender(() => {
          this.toolbar()?.nativeElement.querySelector<HTMLButtonElement>('[data-overflow] button, [data-clear] button')?.focus();
        }, { injector: this.injector });
      }
    });
  }
}
